import React, { useState, useCallback, useMemo, forwardRef } from 'react';
import { View, StyleSheet, Dimensions, Alert, ActivityIndicator, TouchableOpacity, Linking } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import BottomSheet, { BottomSheetView, BottomSheetBackdrop, BottomSheetScrollView } from '@gorhom/bottom-sheet';
import { Ionicons } from '@expo/vector-icons';
import { Svg, Path } from 'react-native-svg';
import { ThemedText } from './ThemedText';
import ClipboardService from '@/services/clipboardService';
import BottomSheetHeader from './BottomSheetHeader';
import { CheckinService } from '@/services/checkinService';
import { Meeting } from '@/types/meetings';
import { useAuth } from '@/contexts/AuthContext';
import { useNotes } from '@/contexts/NotesContext';
import { getMeetingTimeStatus, formatDisplayTime } from '@/utils/meetingUtils';
import CachedImage from './CachedImage';
import { SavedMeetingsService } from '@/services/savedMeetingsService';
import { useLanguage } from '@/contexts/LanguageContext';
import { translateCategory, translatePlatform, translateWeekdays } from '@/utils/meetingLocale';

interface MeetingDetailsDrawerProps {
  isVisible: boolean;
  onClose: () => void;
  meeting: Meeting | null;
  onMeetingSavedToggle?: (meetingId: string, isSaved: boolean) => void;
}


const MeetingDetailsDrawer = forwardRef<BottomSheet, MeetingDetailsDrawerProps>(
  ({ isVisible, onClose, meeting, onMeetingSavedToggle }, ref) => {
    const { user } = useAuth();
    const { addNote } = useNotes();
    const { t } = useLanguage();
    const screenHeight = Dimensions.get('window').height;
    const insets = useSafeAreaInsets();
    
    // State management
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isCheckinSuccess, setIsCheckinSuccess] = useState<boolean>(false);
    const [isSaved, setIsSaved] = useState<boolean>(false);
    const [isReminderLoading, setIsReminderLoading] = useState<boolean>(false);
    const [timeoutId, setTimeoutId] = useState<any>(null);

    // Use dynamic content sizing - let the drawer size to its content
    const calculateContentHeight = () => {
      // Remove hardcoded heights and let content determine size
      const maxSheetHeight = screenHeight * 0.85; // Max 85% for better UX
      
      return {
        maxHeight: maxSheetHeight,
        needsScrolling: false // We'll size to content, no scrolling needed
      };
    };

    // Calculate snap points - use proper height to ensure header and button are visible
    const modalSnapPoints = useMemo(() => {
      // Use 80% as primary snap point to show all content including header and button
      return ['80%']; // Single snap point ensures header and button are always visible
    }, []);


    const handleCheckIn = async () => {
      if (!meeting) return;
      
      // Check if user is authenticated
      if (!user) {
        Alert.alert('Erro', 'É necessário fazer login para realizar o check-in');
        return;
      }
      
      setIsLoading(true);
      
      try {
        // Perform check-in attempt
        const deviceInfo = CheckinService.getDeviceInfo();
        // Use current time in ISO format (UTC)
        const today = new Date().toISOString();
        
        // Perform direct Supabase check-in (simplified approach)
        const result = await CheckinService.checkIn({
          meetingId: meeting.id,
          meetingDate: today,
          deviceInfo,
          notes: 'Check-in via mobile app',
          onNoteCreate: addNote
        });

        // Check if check-in was successful
        if (result.success || result.alreadyCheckedIn) {
          console.log('✅ Check-in successful:', result);
          
          // Show success feedback for 2 seconds
          setIsLoading(false);
          setIsCheckinSuccess(true);
          
          // Wait 2 seconds then redirect to meeting
          const id = setTimeout(async () => {
            if (isVisible) { // Only redirect if drawer is still visible
              await handleJoinMeeting();
              setIsCheckinSuccess(false);
            }
          }, 2000);
          setTimeoutId(id);
        } else {
          console.log('⚠️ Check-in failed but proceeding to meeting:', result);
          // If check-in failed, show error but still proceed to meeting
          Alert.alert(
            'Aviso', 
            'Não foi possível realizar o check-in, mas você ainda pode acessar o encontro.',
            [
              {
                text: 'OK',
                onPress: async () => {
                  await handleJoinMeeting();
                }
              }
            ]
          );
        }
      } catch (error) {
        console.log('❌ Check-in error but proceeding to meeting:', error);
        setIsLoading(false);
        
        // Show error alert but still allow access to meeting
        Alert.alert(
          'Erro', 
          'Ocorreu um erro ao fazer check-in, mas você ainda pode acessar o encontro.',
          [
            {
              text: 'OK',
              onPress: async () => {
                await handleJoinMeeting();
              }
            }
          ]
        );
      }
    };

    const handleJoinMeeting = async () => {
      // PRD §5.6 — `meeting_join_tapped`. Fired at handler entry so we
      // catch the tap even if the link is missing or `Linking.openURL`
      // throws below. The drawer's "Entrar" button opens the external
      // meeting URL directly, so source/type are `'external'`.
      import('@/utils/clarityEvents')
        .then(({ logMeetingJoinTapped }) =>
          logMeetingJoinTapped({
            meeting_source: 'external',
            meeting_type: 'external',
            meeting_id: meeting?.id,
          })
        )
        .catch(() => {});

      try {
        if (!meeting?.meeting_link) {
          Alert.alert('Aviso', 'Link do encontro não disponível');
          return;
        }

        // Clean and validate the URL
        let cleanUrl = meeting.meeting_link.trim();
        
        // Remove any potential file:// prefix if it exists
        if (cleanUrl.startsWith('file://')) {
          const urlPart = cleanUrl.split('/').pop() || '';
          cleanUrl = urlPart;
        }
        
        // Ensure URL has protocol - add https:// if missing
        let finalUrl = cleanUrl;
        if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
          finalUrl = `https://${finalUrl}`;
        }
        
        console.log('🔗 MeetingDetailsDrawer: Opening URL:', finalUrl);
        await Linking.openURL(finalUrl);
        
        // Close the drawer after opening the link
        onClose();
      } catch (error) {
        console.error('Error opening meeting link:', error);
        Alert.alert('Erro', 'Não foi possível abrir o link do encontro');
      } finally {
        // Always reset loading state regardless of success/failure
        setIsLoading(false);
      }
    };

    // Copy password to clipboard using production-ready service
    const handleCopyPassword = async () => {
      if (!meeting?.meeting_password) return;
      
      await ClipboardService.copyWithFeedback(
        meeting.meeting_password,
        'Senha copiada para a área de transferência!',
        'Senha do encontro'
      );
    };

    // Load saved meeting status
    const loadSavedStatus = useCallback(async () => {
      if (!user?.id || !meeting?.id) return;
      
      try {
        const savedMeetings = await SavedMeetingsService.getSavedMeetings(user.id);
        setIsSaved(savedMeetings.includes(meeting.id));
      } catch (error) {
        console.error('Failed to load saved meeting status:', error);
      }
    }, [user?.id, meeting?.id]);

    // Handle reminder toggle (save/unsave meeting) - same as meeting cards
    const handleReminderToggle = useCallback(async () => {
      if (!user?.id || !meeting?.id) return;
      
      setIsReminderLoading(true);
      try {
        const result = await SavedMeetingsService.toggleSavedMeeting(user.id, meeting.id);
        
        if (result.success) {
          setIsSaved(result.isSaved);
          
          // Notify parent component to update saved meetings list
          onMeetingSavedToggle?.(meeting.id, result.isSaved);
          
          console.log(result.isSaved ? '✅ Meeting reminder scheduled' : '✅ Meeting reminder canceled');
        } else {
          console.error('Failed to toggle meeting reminder');
        }
      } catch (error) {
        console.error('Failed to toggle reminder:', error);
      } finally {
        setIsReminderLoading(false);
      }
    }, [user?.id, meeting?.id, onMeetingSavedToggle]);

    // Load saved status when component mounts or meeting changes
    React.useEffect(() => {
      if (isVisible) {
        loadSavedStatus();
      }
    }, [isVisible, loadSavedStatus]);

    // Get meeting time status and formatted weekdays
    const meetingTimeStatus = meeting?.meeting_time ? getMeetingTimeStatus(meeting.meeting_time, meeting.meeting_weekday, t) : null;
    
    // Check if meeting time difference is greater than 2 hours
    const shouldShowReminder = useMemo(() => {
      if (!meeting?.meeting_time) return false;
      
      try {
        const [hourStr, minuteStr] = meeting.meeting_time.split(':');
        const meetingHour = parseInt(hourStr, 10);
        const meetingMinute = parseInt(minuteStr, 10);
        const meetingTimeInMinutes = meetingHour * 60 + meetingMinute;
        
        const now = new Date();
        const currentTimeInMinutes = now.getHours() * 60 + now.getMinutes();
        const timeDifferenceInMinutes = Math.abs(meetingTimeInMinutes - currentTimeInMinutes);
        
        // Show reminder if difference is greater than 2 hours (120 minutes)
        return timeDifferenceInMinutes > 120;
      } catch (error) {
        console.error('Error calculating time difference:', error);
        return false;
      }
    }, [meeting?.meeting_time]);
    const formattedWeekdays = meeting?.meeting_weekday ? translateWeekdays(meeting.meeting_weekday, t, false) : '';

    // Callbacks
    const handleSheetChanges = useCallback((index: number) => {
      if (index === -1) {
        // Clear timeout if drawer is closing
        if (timeoutId) {
          clearTimeout(timeoutId);
          setTimeoutId(null);
        }
        setIsCheckinSuccess(false);
        onClose();
      }
    }, [onClose, timeoutId]);

    const renderBackdrop = useCallback(
      (props: any) => (
        <BottomSheetBackdrop
          {...props}
          disappearsOnIndex={-1}
          appearsOnIndex={0}
          opacity={0.4}
          enableTouchThrough={false}
          pressBehavior="close"
          style={[props.style, { height: screenHeight }]}
        />
      ),
      [screenHeight]
    );

    // Check if meeting is within 2-hour live window using centralized logic
    const isMeetingInLiveWindow = useCallback((meetingTime: string, meetingWeekday: string): boolean => {
      const timeStatus = getMeetingTimeStatus(meetingTime, meetingWeekday, t);
      return timeStatus.status === 'ongoing';
    }, [t]);

    // Don't render when not visible or no meeting data
    if (!isVisible || !meeting) {
      return null;
    }

    const renderContent = () => {
      return (
        <View style={[styles.figmaContentContainer, { backgroundColor: '#F9F8F3' }]}>
          {/* Figma: Curved Background Illustration Section */}
          <View style={[styles.figmaIllustrationSection, { backgroundColor: '#FFFFFF' }]}>
            <View style={[styles.figmaBackgroundCircle, { backgroundColor: '#F9F8F3' }]} />
            <View style={styles.figmaIllustrationContainer}>
              <CachedImage 
                source={require('@/assets/images/checkin-illustration.png')}
                style={styles.figmaCheckinIllustration}
                resizeMode="contain"
              />
            </View>
          </View>

          {/* Figma: Meeting Information Section */}
          <View style={[styles.figmaMeetingContent, { backgroundColor: '#FFFFFF' }]}>
            {/* Figma: Status Tags Container */}
            <View style={styles.figmaTagsContainer}>
              {/* Time Status Chip using main chip component styling */}
              {meetingTimeStatus && (
                <View style={meetingTimeStatus.status === 'ongoing' ? 
                  [styles.drawerTimeTag, styles.drawerTimeTagLive] : 
                  styles.drawerTimeTag
                }>
                  <ThemedText style={meetingTimeStatus.status === 'ongoing' ? 
                    [styles.drawerTimeText, styles.drawerTimeTextLive] : 
                    styles.drawerTimeText
                  }>
                    {meetingTimeStatus.status === 'ongoing' ? t('meetings.status.live') : meetingTimeStatus.message}
                  </ThemedText>
                </View>
              )}
              
              {/* Meeting Time Chip (only show if not live) */}
              {meetingTimeStatus?.status !== 'ongoing' && (
                <View style={styles.drawerTimeTag}>
                  <ThemedText style={styles.drawerTimeText}>{formatDisplayTime(meeting.meeting_time)}</ThemedText>
                </View>
              )}
            </View>

            {/* Figma: Meeting Title and Info */}
            <View style={styles.figmaMeetingInfo}>
              <ThemedText style={styles.figmaMeetingTitle}>{meeting.meeting_name}</ThemedText>
              
              <View style={[styles.figmaMeetingMeta, { marginBottom: 24 }]}>
                <ThemedText style={[styles.figmaMeetingCategory, { color: '#151515' }]}>{translateCategory(meeting.meeting_category, t)}</ThemedText>
                <ThemedText style={[styles.figmaMeetingPlace, { color: '#151515' }]}>{translatePlatform(meeting.meeting_place, t)}</ThemedText>
              </View>
            </View>
          </View>

          {/* Figma: Divider */}
          <View style={[styles.figmaDivider, { backgroundColor: '#E7EAED' }]} />

          {/* Figma: Schedule and Password Section */}
          <View style={[styles.figmaDetailsSection, { backgroundColor: '#FFFFFF' }]}>
            {/* Schedule */}
            <View style={styles.figmaScheduleRow}>
              <ThemedText style={[styles.figmaScheduleLabel, { color: '#151515' }]}>{t('meetings.labels.schedule')}</ThemedText>
              <ThemedText style={[styles.figmaScheduleValue, { color: '#151515' }]}>{formattedWeekdays}</ThemedText>
            </View>

            {/* Password */}
            {meeting.meeting_password && (
              <View style={styles.figmaPasswordRow}>
                <ThemedText style={[styles.figmaPasswordLabel, { color: '#151515' }]}>{t('meetings.labels.password')}</ThemedText>
                <ThemedText style={[styles.figmaPasswordValue, { color: '#151515' }]}>{meeting.meeting_password}</ThemedText>
                <TouchableOpacity 
                  style={styles.figmaCopyButton}
                  onPress={handleCopyPassword}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <Path d="M15.4942 5.74802C15.2398 4.10756 14.0585 3 12.2783 3H6.50363C4.49334 3 3.23047 4.42788 3.23047 6.44497V11.8837C3.23047 13.7213 4.28076 15.0754 6.00276 15.2889" stroke={'#151515'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <Path fillRule="evenodd" clipRule="evenodd" d="M17.913 8.67969H12.1403C10.1291 8.67969 8.86719 10.1037 8.86719 12.1198V17.5586C8.86719 19.5747 10.1223 20.9987 12.1403 20.9987H17.912C19.9311 20.9987 21.1862 19.5747 21.1862 17.5586V12.1198C21.1862 10.1037 19.9311 8.67969 17.913 8.67969Z" stroke={'#151515'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </Svg>
                </TouchableOpacity>
              </View>
            )}
          </View>
        </View>
      );
    };

    const renderCheckinContent = () => {
      // Show reminder button if time difference > 2 hours
      if (shouldShowReminder) {
        return (
          <View style={styles.figmaActionContainer}>
            <TouchableOpacity 
              style={[styles.figmaReminderButton, { borderColor: '#151515' }]}
              onPress={handleReminderToggle}
              disabled={isReminderLoading}
            >
              {isReminderLoading ? (
                <ActivityIndicator size="small" color={'#151515'} />
              ) : (
                <>
                  <Svg width="17" height="20" viewBox="0 0 17 20" fill="none">
                    <Path fillRule="evenodd" clipRule="evenodd" d="M8.5 15.8476C14.1392 15.8476 16.7481 15.1242 17 12.2205C17 9.3188 15.1812 9.50539 15.1812 5.94511C15.1812 3.16414 12.5452 0 8.5 0C4.45477 0 1.81885 3.16414 1.81885 5.94511C1.81885 9.50539 0 9.3188 0 12.2205C0.252952 15.1352 2.86177 15.8476 8.5 15.8476Z" stroke={'#151515'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <Path d="M10.8889 18.8574C9.52473 20.3721 7.39672 20.3901 6.01953 18.8574" stroke={'#151515'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </Svg>
                  <ThemedText style={[styles.figmaReminderButtonText, { color: '#151515' }]}>
                    {isSaved ? 'Desativar' : 'Avise-me'}
                  </ThemedText>
                </>
              )}
            </TouchableOpacity>
          </View>
        );
      }

      // Show success button for 2 seconds after successful check-in
      if (isCheckinSuccess) {
        return (
          <View style={styles.figmaActionContainer}>
            <View style={styles.figmaSuccessButton}>
              <Svg width="25" height="24" viewBox="0 0 25 24" fill="none">
                <Path d="M19.5 7.1875L9.875 16.8125L5.5 12.4375" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </Svg>
              <ThemedText style={styles.figmaSuccessButtonText}>Check-in realizado</ThemedText>
            </View>
          </View>
        );
      }

      // Show check-in button (with loading state when processing)
      return (
        <View style={styles.figmaActionContainer}>
          <TouchableOpacity 
            style={[
              styles.figmaPrimaryButton, 
              { 
                backgroundColor: isLoading ? `${'#151515'}66` : '#151515' // 40% opacity when loading
              }
            ]}
            onPress={handleCheckIn}
            disabled={isLoading}
          >
            <Svg width="25" height="24" viewBox="0 0 25 24" fill="none">
              <Path d="M5.47266 10.5269C5.47266 15.8822 11.794 21 12.9986 21C14.2031 21 20.5245 15.8822 20.5245 10.5269C20.5245 6.36941 17.1551 3 12.9986 3C8.84207 3 5.47266 6.36941 5.47266 10.5269Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <Path fillRule="evenodd" clipRule="evenodd" d="M15.506 10.4654C15.506 9.07984 14.3831 7.95703 12.9976 7.95703C11.6111 7.95703 10.4883 9.07984 10.4883 10.4654C10.4883 11.8509 11.6111 12.9747 12.9976 12.9747C14.3831 12.9747 15.506 11.8509 15.506 10.4654Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </Svg>
            <ThemedText style={[styles.figmaPrimaryButtonText, { color: '#FFFFFF' }]}>
              {isLoading ? 'Realizando seu check-in...' : 'Fazer Check-in'}
            </ThemedText>
          </TouchableOpacity>
        </View>
      );
    };

    return (
      <BottomSheet
        ref={ref}
        index={0}
        snapPoints={modalSnapPoints}
        onChange={handleSheetChanges}
        enablePanDownToClose
        enableDynamicSizing={false}
        backgroundStyle={[styles.bottomSheetBackground, { backgroundColor: '#F9F8F3' }]}
        handleIndicatorStyle={[styles.handleIndicator, { backgroundColor: '#DAD8CF' }]}
        backdropComponent={renderBackdrop}
        animateOnMount={true}
        android_keyboardInputMode="adjustResize"
        enableContentPanningGesture={false}
        enableHandlePanningGesture={true}
        activeOffsetY={[-1, 1]}
        style={{ zIndex: 200, elevation: 200 }}
        failOffsetX={[-5, 5]}
      >
        <BottomSheetView style={[styles.sheetContainer, { backgroundColor: '#F9F8F3' }]}>
          {/* Figma: Header */}
          <View style={[styles.figmaHeaderSection, { backgroundColor: '#F9F8F3' }]}>
            <BottomSheetHeader 
              title="Check-in" 
              onClose={onClose} 
            />
          </View>

          {/* Content - Fixed layout, no scrolling, hugs content */}
          <View style={styles.figmaFixedContent}>
            {renderContent()}
            
            {/* Figma: Check-in Button Section */}
            <View style={[styles.figmaCheckinButtonSection, { backgroundColor: '#FFFFFF', paddingBottom: Math.max(insets.bottom, 16) }]}>
              {renderCheckinContent()}
            </View>
          </View>
        </BottomSheetView>
      </BottomSheet>
    );
  }
);

MeetingDetailsDrawer.displayName = 'MeetingDetailsDrawer';

export default MeetingDetailsDrawer;

const styles = StyleSheet.create({
  // Figma: Bottom Sheet Base Styles
  bottomSheetBackground: {
    backgroundColor: '#F9F8F3', // Colors-Bege-Spring-Wood
    borderTopLeftRadius: 16, // Spacing-spacing-small
    borderTopRightRadius: 16,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: -4,
    },
    shadowOpacity: 0.1,
    shadowRadius: 24,
    elevation: 24,
  },
  handleIndicator: {
    backgroundColor: '#DAD8CF', // Colors-Bege-Westar
    width: 40,
    height: 4,
  },
  sheetContainer: {
    flex: 1,
    backgroundColor: '#F9F8F3', // Colors-Bege-Spring-Wood
  },

  // Figma: Header Section
  figmaHeaderSection: {
    flexShrink: 0,
    backgroundColor: '#F9F8F3', // Colors-Bege-Spring-Wood
    minHeight: 56, // Ensure header has proper height
    justifyContent: 'center', // Center the header content
    zIndex: 999, // Ensure header section is above content
    position: 'relative', // Establish stacking context
    elevation: 5, // For Android
  },

  // Figma: Content Container
  figmaContentContainer: {
    flex: 1,
    backgroundColor: '#F9F8F3', // Colors-Bege-Spring-Wood
  },

  // Figma: Curved Background Illustration Section (Group_5573_8295)
  figmaIllustrationSection: {
    backgroundColor: '#FFFFFF', // Colors-Neutral-White
    width: '100%',
    paddingHorizontal: 0, // Remove padding for full-width image
    paddingVertical: 16,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },

  // Figma: Large Beige Circular Background (Background_5573_8296)
  figmaBackgroundCircle: {
    backgroundColor: '#F9F8F3', // Colors-Bege-Spring-Wood
    borderRadius: 651.825, // 1303.65px / 2
    width: 1303.65,
    height: 1303.65,
    position: 'absolute',
    top: -1105.65,
    left: -455.324,
  },

  // Figma: Illustration Container
  figmaIllustrationContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
  },

  // Figma: Check-in Illustration - Full Width
  figmaCheckinIllustration: {
    width: '100%',
    aspectRatio: 786 / 396, // Maintain proper aspect ratio
    alignSelf: 'stretch',
  },

  // Figma: Meeting Content Section (Group_5573_8301)
  figmaMeetingContent: {
    backgroundColor: '#FFFFFF', // Colors-Neutral-White
    paddingHorizontal: 24, // Spacing-spacing-large
    paddingTop: 24,
    paddingBottom: 0,
    gap: 24, // Spacing-spacing-large
  },

  // Figma: Status Tags Container (Place_5573_8302)
  figmaTagsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8, // Spacing-spacing-xxsmall
    width: '100%',
  },


  // Figma: Meeting Information Container (Group_5574_7834)
  figmaMeetingInfo: {
    alignItems: 'flex-start',
    gap: 8, // Spacing-spacing-xxsmall
  },

  // Figma: Meeting Title (GrupoIndependência_5573_8307)
  figmaMeetingTitle: {
    // color: Uses ThemedText default theme color (no override needed)
    textAlign: 'center',
    width: '100%',
    fontFamily: 'Sentient-Regular',
    fontSize: 24,
    lineHeight: 28.8, // 120% of 24px
  },

  // Figma: Meeting Meta Container (Group_5573_8308)
  figmaMeetingMeta: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8, // Spacing-spacing-xxsmall
    width: '100%',
    height: 20,
  },

  // Figma: Meeting Category (Partilhas_5573_8309)
  figmaMeetingCategory: {
    // color: Overridden at render time with '#151515'
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    lineHeight: 19.6, // 140% of 14px
  },

  // Figma: Meeting Place (GoogleMeet_5573_8310)
  figmaMeetingPlace: {
    // color: Overridden at render time with '#151515'
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6, // 140% of 14px
  },

  // Figma: Divider (Divider_5577_7893)
  figmaDivider: {
    backgroundColor: '#E7EAED', // Colors-Neutral-Mystic
    height: 1,
    width: '100%',
    marginHorizontal: 24,
  },

  // Figma: Details Section Container (Group_5575_7871)
  figmaDetailsSection: {
    backgroundColor: '#FFFFFF', // Colors-Neutral-White
    paddingHorizontal: 24, // Spacing-spacing-large
    paddingTop: 24,
    paddingBottom: 24,
    gap: 24, // Spacing-spacing-large
  },

  // Figma: Schedule Row (Group_5576_8495)
  figmaScheduleRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8, // Spacing-spacing-xxsmall
    width: '100%',
    minHeight: 20, // Allow expansion for multiline text
  },

  // Figma: Schedule Label (Horários_5576_8496)
  figmaScheduleLabel: {
    // color: Overridden at render time with '#151515'
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    lineHeight: 14, // 100% of 14px
  },

  // Figma: Schedule Value (DeSegundaFeiraASextaFeira_5576_8497)
  figmaScheduleValue: {
    // color: Overridden at render time with '#151515'
    textAlign: 'right',
    flex: 1,
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 19.6, // 140% of 14px
  },

  // Figma: Password Row (Group_5576_8499)
  figmaPasswordRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8, // Spacing-spacing-xxsmall
    width: '100%',
    height: 24,
    overflow: 'hidden',
  },

  // Figma: Password Label (SenhaDeAcesso_5576_8500)
  figmaPasswordLabel: {
    // color: Overridden at render time with '#151515'
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    lineHeight: 14, // 100% of 14px
  },

  // Figma: Password Value (SenhaPadrinho123_5576_8501)
  figmaPasswordValue: {
    // color: Overridden at render time with '#151515'
    textAlign: 'right',
    flex: 1,
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 19.6, // 140% of 14px
  },

  // Main chip component styling for drawer
  drawerTimeTag: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#DAD8CF',
    borderRadius: 52,
    paddingVertical: 8,
    paddingHorizontal: 12,
    alignSelf: 'flex-start',
  },
  drawerTimeTagLive: {
    backgroundColor: '#669AB7', // Blue background for live meetings
  },
  drawerTimeText: {
    fontSize: 12,
    fontFamily: 'Inter_700Bold',
    color: '#56554D',
    lineHeight: 16.8,
  },
  drawerTimeTextLive: {
    color: '#ffffff', // White text for live meetings
  },

  // Figma: Copy Button (Copy_5576_8503)
  figmaCopyButton: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },

  // Figma: Check-in Button Section (Section_5573_8311)
  figmaCheckinButtonSection: {
    backgroundColor: '#FFFFFF', // Colors-Neutral-White
    paddingHorizontal: 24, // Spacing-spacing-large
    paddingTop: 24,
    gap: 12, // Spacing-spacing-xsmall
  },

  // Figma: Action Container
  figmaActionContainer: {
    width: '100%',
    gap: 12, // Spacing-spacing-xsmall
  },

  // Figma: Reminder Button (Secondary Style)
  figmaReminderButton: {
    height: 48,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#015480', // Colors-Blue-Blumine
    backgroundColor: 'transparent',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8, // Spacing-spacing-xxsmall
    paddingHorizontal: 16, // Spacing-spacing-small
  },

  // Figma: Reminder Button Text
  figmaReminderButtonText: {
    color: '#015480', // Colors-Blue-Blumine
    textAlign: 'center',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6, // 140% of 14px
  },

  // Figma: Primary Button (Button_5573_8313)
  figmaPrimaryButton: {
    backgroundColor: '#015480', // Colors-Blue-Blumine
    borderRadius: 12, // Spacing-spacing-xsmall
    paddingVertical: 16, // Spacing-spacing-small
    paddingHorizontal: 16,
    height: 48,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8, // Spacing-spacing-xxsmall
    flex: 1,
  },

  // Figma: Primary Button Text (Button_598_1070)
  figmaPrimaryButtonText: {
    // color: Overridden at render time with '#FFFFFF'
    textAlign: 'center',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6, // 140% of 14px
  },

  // Figma: Secondary Button (Button_5573_8312)
  figmaSecondaryButton: {
    borderWidth: 1,
    borderColor: '#C0D1DB', // Colors-Blue-Jungle-Mist
    borderRadius: 12, // Spacing-spacing-xsmall
    paddingVertical: 16, // Spacing-spacing-small
    paddingHorizontal: 16,
    minHeight: 56,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12, // Spacing-spacing-xsmall
  },

  // Figma: Secondary Button Text
  figmaSecondaryButtonText: {
    color: '#015480', // Colors-Blue-Blumine
    textAlign: 'center',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6, // 140% of 14px
  },

  // Figma: Button Group
  figmaButtonGroup: {
    gap: 12, // Spacing-spacing-xsmall
  },

  // Figma: Loading Container
  figmaLoadingContainer: {
    alignItems: 'center',
    gap: 16,
    paddingVertical: 32,
  },

  // Figma: Loading Text
  figmaLoadingText: {
    fontSize: 16,
    fontFamily: 'Inter_400Regular',
    // color: Overridden at render time with '#151515'
  },

  // Figma: Success Container
  figmaSuccessContainer: {
    alignItems: 'center',
    gap: 16,
    paddingVertical: 32,
  },

  // Figma: Success Title
  figmaSuccessTitle: {
    fontSize: 18,
    fontFamily: 'Inter_700Bold',
    // color: Overridden at render time with '#10B981'
    textAlign: 'center',
  },

  // Figma: Success Subtitle
  figmaSuccessSubtitle: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    // color: Overridden at render time with '#151515'
    textAlign: 'center',
  },

  // Figma: Error Container
  figmaErrorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },

  // Figma: Error Text
  figmaErrorText: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    // color: Overridden at render time with '#EF4444'
    flex: 1,
  },

  // Figma: Fixed Content Container
  figmaFixedContent: {
    flex: 1,
  },

  // Scrollable content
  scrollableContent: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },

  // Legacy styles for loading state
  statusContainer: {
    alignItems: 'center',
    gap: 16,
    paddingVertical: 32,
  },
  statusText: {
    fontSize: 16,
    fontFamily: 'Inter_400Regular',
    color: '#151515',
  },
  successText: {
    fontSize: 16,
    fontFamily: 'Inter_500Medium',
    color: '#10B981',
  },
  checkinTime: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#A19F97',
    marginTop: 4,
  },
  actionContainer: {
    gap: 16,
  },
  successContainer: {
    alignItems: 'center',
    gap: 12,
    paddingVertical: 24,
  },
  successTitle: {
    fontSize: 20,
    fontFamily: 'Sentient-Bold',
    color: '#10B981',
    textAlign: 'center',
  },
  successSubtitle: {
    fontSize: 16,
    fontFamily: 'Inter_400Regular',
    color: '#A19F97',
    textAlign: 'center',
  },
  errorInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  errorText: {
    fontSize: 16,
    fontFamily: 'Inter_400Regular',
    color: '#EF4444',
    flex: 1,
  },
  buttonGroup: {
    gap: 12,
  },
  // New Figma design styles
  newContentContainer: {
    flex: 1,
  },
  illustrationSection: {
    alignItems: 'center',
    justifyContent: 'center',
    height: 200,
    position: 'relative',
    backgroundColor: '#F8F8F7',
    marginHorizontal: 24,
    marginTop: 8,
    borderRadius: 16,
  },
  backgroundCircle: {
    position: 'absolute',
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: '#E8F4F8',
  },
  checkinIllustration: {
    width: 160,
    height: 160,
    zIndex: 1,
  },
  meetingContent: {
    padding: 24,
    gap: 20,
  },
  tagsContainer: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  liveTag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#E8F4F8',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  liveTagText: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#015480',
  },
  timeTag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  ongoingTag: {
    backgroundColor: '#FEF3C7',
  },
  upcomingTag: {
    backgroundColor: '#E5E7EB',
  },
  timeTagText: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#374151',
  },
  meetingInfo: {
    gap: 8,
  },
  meetingTitle: {
    fontSize: 24,
    fontFamily: 'Sentient-Bold',
    color: '#151515',
    lineHeight: 32,
  },
  meetingMeta: {
    gap: 4,
  },
  meetingCategory: {
    fontSize: 16,
    fontFamily: 'Inter_500Medium',
    color: '#015480',
  },
  meetingPlace: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#A19F97',
  },
  divider: {
    height: 1,
    backgroundColor: '#E5E5E5',
    marginVertical: 8,
  },
  scheduleSection: {
    gap: 12,
  },
  scheduleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  scheduleLabel: {
    fontSize: 16,
    fontFamily: 'Inter_500Medium',
    color: '#151515',
  },
  scheduleValue: {
    fontSize: 16,
    fontFamily: 'Inter_400Regular',
    color: '#A19F97',
    textAlign: 'right',
    flex: 1,
    marginLeft: 16,
  },
  passwordSection: {
    gap: 8,
  },
  passwordLabel: {
    fontSize: 16,
    fontFamily: 'Inter_500Medium',
    color: '#151515',
  },
  passwordRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#F8F8F7',
    padding: 12,
    borderRadius: 8,
  },
  passwordValue: {
    fontSize: 16,
    fontFamily: 'Inter_600SemiBold',
    color: '#151515',
    letterSpacing: 2,
  },
  copyButton: {
    padding: 4,
  },
  checkinButtonSection: {
    paddingHorizontal: 24,
    paddingTop: 16,
    backgroundColor: '#FFFFFF',
    borderTopWidth: 1,
    borderTopColor: '#E5E5E5',
  },
  // Meeting card time tag styles (matching meeting cards exactly)
  meetingCardTimeTag: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#DAD8CF',
    borderRadius: 52,
    paddingVertical: 8,
    paddingHorizontal: 12, // Fixed: removed paddingLeft override for balanced padding
    gap: 8,
    alignSelf: 'flex-start',
  },
  meetingCardTimeText: {
    fontSize: 12,
    fontFamily: 'Inter_700Bold',
    color: '#56554D',
    lineHeight: 16.8,
  },

  // Figma: Success Button Styles
  figmaSuccessButton: {
    backgroundColor: '#0fa971', // Jade green as specified
    height: 48, // Height as specified
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12, // 12px border radius as specified in design
    padding: 16, // 16px padding as specified
    gap: 8, // 8px gap between icon and text as specified
    width: '100%',
  },
  figmaSuccessButtonText: {
    color: '#FFFFFF', // White text as specified
    fontFamily: 'Inter_500Medium',
    fontSize: 14, // 14px as specified in design
    lineHeight: 19.6, // 140% of 14px = 19.6px
    textAlign: 'center',
  },
});